import type { Tone } from "../Troubleshooting/TroubleshootingUi";
import { VpnStatusRow } from "./vpnStatusApi";

/**
 * Whether the VPN page is offered: the user can see at least one VPN
 * (`permission.vpn_status` from user/info, ENG-2531). Absent = hidden, so an
 * admin without the flag never shows an empty page.
 */
export const canSeeVpnStatus = (permission: unknown): boolean =>
  Boolean(
    (permission as Record<string, unknown> | null | undefined)?.vpn_status
  );

export const KIND_LABEL: Record<VpnStatusRow["kind"], string> = {
  wireguard: "WireGuard",
  ipsec: "IPsec site-to-site",
};

/** A VPN with one of two IPsec tunnels down still carries traffic, without redundancy. */
export const isPartial = (vpn: VpnStatusRow): boolean =>
  vpn.status === "up" &&
  vpn.tunnels_total != null &&
  vpn.tunnels_total > 1 &&
  vpn.tunnels_up != null &&
  vpn.tunnels_up < vpn.tunnels_total;

export const statusTone = (vpn: VpnStatusRow): Tone => {
  if (vpn.status === "down") return "bad";
  if (vpn.status === "unknown") return "grey";
  return isPartial(vpn) ? "warn" : "ok";
};

export const statusLabel = (vpn: VpnStatusRow): string => {
  if (vpn.status === "down") return "Down";
  if (vpn.status === "unknown") return "Status unknown";
  if (isPartial(vpn))
    return `Up · ${vpn.tunnels_up} of ${vpn.tunnels_total} tunnels`;
  return "Up";
};

/** The timestamp shown next to the status, and what it means for this kind. */
export const lastSeen = (
  vpn: VpnStatusRow
): { label: string; iso: string | null } =>
  vpn.kind === "wireguard"
    ? { label: "Last handshake", iso: vpn.last_handshake }
    : { label: "Last status change", iso: vpn.last_change };

export const downVpns = (vpns: VpnStatusRow[]): VpnStatusRow[] =>
  vpns.filter((vpn) => vpn.status === "down");

/** Sites behind any down VPN, de-duplicated, in name order. */
export const affectedSites = (vpns: VpnStatusRow[]) => {
  const seen = new Map<number, string>();
  for (const vpn of downVpns(vpns)) {
    for (const site of vpn.sites ?? []) seen.set(site.id, site.name);
  }
  return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
};

/** Show the owning account only when the list spans more than one (staff). */
export const showsGroups = (vpns: VpnStatusRow[]): boolean =>
  new Set(vpns.map((vpn) => vpn.group?.id ?? null)).size > 1;
